import type { McpTool } from "@p0security/shared/types/agentic/mcp";

import { jiraAiMcpTools } from "./mcp-tools.ts";

/** The access levels of P0's own roles in a company-managed project. The
 * connector names the role behind each. */
export const JiraAiLevels = ["read", "write"] as const;
export type JiraAiLevel = (typeof JiraAiLevels)[number];

/** Tools whose effect reaches past one project, which a project role can't
 * bound: they read or change users, projects, screens or shared boards
 * across the site. */
const GLOBAL_PERMISSION_TOOLS: readonly string[] = [
  "createJiraBoard",
  "createJiraProject",
  "getJiraScreen",
  "getJiraUser",
  "lookupJiraAccountId",
  "updateJiraScreen",
];

/** Tools that need the Administer Projects permission, which P0's write role
 * doesn't hold: managing a project's versions. Related work on a version
 * needs only Resolve and Edit issues, so it isn't one. */
const PROJECT_ADMIN_TOOLS: readonly string[] = ["manageJiraProjectVersion"];

/** Tools that need Jira's global Bulk change permission, which no project
 * role can give: converting a work item's hierarchy level goes through Jira's
 * Move in company-managed spaces. */
const BULK_CHANGE_TOOLS: readonly string[] = ["convertJiraIssueHierarchy"];

// A tool named for removing something, which with no annotation counts as
// destructive.
const DELETE_LIKE_NAME = /delete|remove|purge|destroy|archive/i;

// Jira's own tools carry "Jira" in their names. The rest belong to other
// Atlassian products, or to all of them.
const JIRA_TOOL_NAME = /jira/i;

const toolsByName = new Map(jiraAiMcpTools.map((tool) => [tool.name, tool]));

/** Whether `tool` may change or delete data with no way back: an explicit
 * `destructiveHint: true`, or, with no annotations at all, a delete-like name.
 *
 * MCP defaults `destructiveHint` to true, but this ignores that default. The
 * static table annotates every tool, so applying it would make each annotated
 * write tool that omits the hint ungrantable. INT-609's live capture must be
 * checked for write tools without a `destructiveHint`. */
const isDestructive = ({ annotations, name }: McpTool) =>
  annotations
    ? annotations.destructiveHint === true
    : DELETE_LIKE_NAME.test(name);

/**
 * Why an agent can't be granted the tool `name`, or `undefined` when it can.
 *
 * Refused: tools P0 doesn't know, other products' tools, `discover` and
 * `execute*` (which reach other tools by name), tools that need project
 * admin or Bulk change, destructive tools, and tools that act beyond one
 * project.
 */
export const toolRejection = (name: string): string | undefined => {
  if (name === "discover" || name.startsWith("execute")) {
    return `The tool ${name} runs other tools, which P0 can't limit to one project.`;
  }
  const tool = toolsByName.get(name);
  if (!tool) return `${name} isn't a Jira tool P0 can grant.`;
  if (!JIRA_TOOL_NAME.test(name)) {
    return `${name} isn't a Jira tool. P0 grants Jira tools only.`;
  }
  if (PROJECT_ADMIN_TOOLS.includes(name)) {
    return `${name} needs Jira's Administer Projects permission, so P0 doesn't grant it to agents.`;
  }
  if (BULK_CHANGE_TOOLS.includes(name)) {
    return `${name} needs Jira's global Bulk change permission, so P0 doesn't grant it to agents.`;
  }
  if (isDestructive(tool)) {
    return `${name} can delete or irreversibly change Jira data, so P0 doesn't grant it to agents.`;
  }
  if (GLOBAL_PERMISSION_TOOLS.includes(name)) {
    return `${name} acts beyond one project, so P0 doesn't grant it to agents.`;
  }
  return undefined;
};

/** The tools an agent can be granted, in the table's order. */
export const grantableJiraAiTools = jiraAiMcpTools.filter(
  ({ name }) => toolRejection(name) === undefined
);

/** Read-only tools that need no project: Jira answers them with what the
 * agent account can already see across the site, so no project role is
 * granted for them. Searches stay out, since they read work items in every
 * open project. */
export const JiraAiSiteWideTools = [
  "getJiraCurrentUser",
  "listJiraBoards",
  "listJiraDashboards",
  "listJiraFilters",
  "listJiraIssueLinkTypes",
  "listJiraProjects",
  "listJiraStatuses",
] as const;

const siteWideTools: readonly string[] = JiraAiSiteWideTools;

/** Whether the tool `name` is in {@link JiraAiSiteWideTools}. A request with
 * no project may hold only these. */
export const isSiteWideTool = (name: string) => siteWideTools.includes(name);

/** The level of P0's role that `tools` need in a company-managed space:
 * read when every tool is marked read-only, otherwise write.
 *
 * @throws Error for a tool P0 doesn't know. Check {@link toolRejection}
 *   first.
 */
export const levelFor = (tools: readonly string[]): JiraAiLevel => {
  const needsWrite = tools.some((name) => {
    const tool = toolsByName.get(name);
    if (!tool) throw new Error(`Unknown Jira tool ${name}`);
    return tool.annotations?.readOnlyHint !== true;
  });
  return needsWrite ? "write" : "read";
};
